import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  FiArrowLeft,
  FiEdit2,
  FiRefreshCw,
  FiMapPin,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiUser,
  FiActivity,
  FiInfo,
  FiUsers,
  FiBox,
  FiTruck,
  FiClipboard,
  FiList,
  FiPause,
  FiPlay,
  FiXCircle,
  FiX,
  FiStar,
  FiFileText,
  FiDownload,
  FiLock,
} from "react-icons/fi";
import { FaIndianRupeeSign } from "react-icons/fa6";
import api from "../../libs/axios";
import { useAuth } from "../../provider/auth.provider";
import { INC_STATUS_OPTIONS, INC_WORKFLOW_OPTIONS } from "./inc_sites";
import TeamTab from "./tabs/TeamTab";
import MaterialsTab from "./tabs/MaterialsTab";
import VisitsTab from "./tabs/VisitsTab";
import MeasurementBookTab from "./tabs/MeasurementBookTab";
import BoqTab from "./tabs/BoqTab";
import BillingTab from "./tabs/BillingTab";
import ClaimsTab from "./tabs/ClaimsTab";
import TimelineTab from "./tabs/TimelineTab";
import { downloadIncFile } from "./inc_downloads";
import MaterialModeBadge, { materialModeLabel } from "./components/MaterialModeBadge";

const formatDate = (d?: string) => {
  if (!d) return "-";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
};
const formatDateTime = (d?: string) => {
  if (!d) return "-";
  const date = new Date(d);
  return isNaN(date.getTime()) ? "-" : date.toLocaleString();
};

const fullName = (u?: any) => (u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.username : "");
const initials = (u?: any) => {
  const f = u?.first_name?.[0] || "";
  const l = u?.last_name?.[0] || "";
  return (f + l || u?.username?.slice(0, 2) || "?").toUpperCase();
};

// ── Status badge ─────────────────────────────────────────────────────────────
const statusColor: Record<string, string> = {
  in_progress: "bg-yellow-100 text-yellow-700",
  hold: "bg-yellow-100 text-yellow-700",
  cancelled: "bg-red-100 text-red-700",
  completed: "bg-green-100 text-green-700",
  closed: "bg-green-100 text-green-700",
};
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const label = INC_STATUS_OPTIONS.find((o) => o.value === status)?.label || (status || "-").replace(/_/g, " ");
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColor[status] || "bg-gray-100 text-gray-700"}`}>
      {label}
    </span>
  );
};

// ── Detail helpers ───────────────────────────────────────────────────────────
const DetailRow: React.FC<{ label: string; value?: any }> = ({ label, value }) => (
  <div className="py-2 border-b border-gray-100 last:border-0">
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className="text-sm text-gray-900 mt-0.5 break-words">{value === 0 || value ? value : "-"}</div>
  </div>
);

const Section: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ title, icon, children, className }) => (
  <div className={`bg-white rounded shadow-sm border border-gray-200 p-4 ${className || ""}`}>
    <h3 className="text-base font-semibold text-gray-900 mb-2 flex items-center gap-2">{icon}{title}</h3>
    {children}
  </div>
);

// ── Dynamic "next workflow action" (mirrors Site Survey's header action button).
// INC advances its workflow as a side-effect of each module's action, so the
// button routes the user to the tab where that action is performed.
type WorkflowAction = { label: string; icon: React.ComponentType<{ className?: string }>; color: string; tab: string; perm: string };
const NEXT_ACTION: Record<string, WorkflowAction> = {
  site_created: { label: "Assign Team", icon: FiUsers, color: "bg-blue-800 hover:bg-blue-900", tab: "team", perm: "assign" },
  team_assigned: { label: "Plan Materials", icon: FiBox, color: "bg-teal-600 hover:bg-teal-700", tab: "materials", perm: "material_planning" },
  material_planned: { label: "Add Site Visit", icon: FiTruck, color: "bg-amber-600 hover:bg-amber-700", tab: "visits", perm: "visits" },
  material_requested: { label: "Add Site Visit", icon: FiTruck, color: "bg-amber-600 hover:bg-amber-700", tab: "visits", perm: "visits" },
  material_issued: { label: "Add Site Visit", icon: FiTruck, color: "bg-amber-600 hover:bg-amber-700", tab: "visits", perm: "visits" },
  material_delivered: { label: "Add Site Visit", icon: FiTruck, color: "bg-amber-600 hover:bg-amber-700", tab: "visits", perm: "visits" },
  work_started: { label: "Update Site Visit", icon: FiTruck, color: "bg-amber-600 hover:bg-amber-700", tab: "visits", perm: "visits" },
  work_in_progress: { label: "Complete Site Visit", icon: FiCheckCircle, color: "bg-amber-600 hover:bg-amber-700", tab: "visits", perm: "visit_check_out" },
  visit_completed: { label: "Create Measurement Book", icon: FiClipboard, color: "bg-violet-600 hover:bg-violet-700", tab: "mb", perm: "measurement_book" },
  measurement_book_pending: { label: "Complete Measurement Book", icon: FiClipboard, color: "bg-violet-600 hover:bg-violet-700", tab: "mb", perm: "mb_approve" },
  measurement_book_approved: { label: "Prepare BOQ", icon: FiList, color: "bg-cyan-600 hover:bg-cyan-700", tab: "boq", perm: "boq" },
  boq_pending: { label: "Finalize BOQ", icon: FiList, color: "bg-cyan-600 hover:bg-cyan-700", tab: "boq", perm: "boq_finalize" },
  boq_finalized: { label: "Create Billing", icon: FaIndianRupeeSign, color: "bg-green-600 hover:bg-green-700", tab: "billing", perm: "billing" },
  billing_pending: { label: "Complete Billing", icon: FaIndianRupeeSign, color: "bg-green-600 hover:bg-green-700", tab: "billing", perm: "billing_complete" },
};

// ── Workflow stepper ─────────────────────────────────────────────────────────
const WORKFLOW_STAGES = INC_WORKFLOW_OPTIONS.filter((o) => o.value !== "cancelled");

// Field work is the one phase of the workflow that is not a single event — it is
// N visits, each with its own status — so the stepper carries the tally and the
// visit list inline instead of making the reader open the Visits tab.
//
// It hangs off Work In Progress ALONE. It used to render on Site Accepted and
// Work Completed as well, which repeated the same table three times in a row and
// made the stepper unreadable; the visits are the same set on every one of them,
// so the middle step — the one that is actually about visits being worked — is
// the only place it belongs.
const VISIT_STAGES = ["work_in_progress"];

// Completed in one go by "Continue without material" — flagged on the stepper
// so a finished material stage is never mistaken for material that moved.
const MATERIAL_STAGES = ["material_planned", "material_requested", "material_issued", "material_delivered"];

export interface VisitSummary {
  total: number;
  planned: number;
  in_progress: number;
  completed: number;
  cancelled: number;
}

export const emptyVisitSummary = (): VisitSummary => ({
  total: 0,
  planned: 0,
  in_progress: 0,
  completed: 0,
  cancelled: 0,
});

export const summariseVisits = (visits: any[]): VisitSummary =>
  visits.reduce((acc: VisitSummary, v: any) => {
    acc.total += 1;
    if (v.visit_status in acc) (acc as any)[v.visit_status] += 1;
    return acc;
  }, emptyVisitSummary());

const visitStatusTone: Record<string, string> = {
  planned: "bg-gray-100 text-gray-700",
  in_progress: "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
};

const VisitCountPills: React.FC<{ summary: VisitSummary }> = ({ summary }) => (
  <div className="flex flex-wrap items-center gap-1.5 mt-2">
    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-200 text-gray-800 font-medium">
      {summary.total} visit{summary.total === 1 ? "" : "s"}
    </span>
    <span className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-700">Planned: {summary.planned}</span>
    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800">In Progress: {summary.in_progress}</span>
    <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Completed: {summary.completed}</span>
    {summary.cancelled > 0 && (
      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">Cancelled: {summary.cancelled}</span>
    )}
  </div>
);

// Compact per-visit lines under the step. Only rendered when the viewer can see
// visits at all; otherwise the pills above stand on their own.
const VisitStepDetails: React.FC<{ visits: any[] }> = ({ visits }) => {
  if (!visits.length) return null;
  return (
    <div className="mt-2 border border-gray-200 rounded overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-gray-50 text-gray-500 uppercase">
          <tr>
            <th className="text-left px-2 py-1">Visit</th>
            {/* visit_date is the date the visit was PLANNED for — the day it was
                actually worked is the check-in stamp in the next column. */}
            <th className="text-left px-2 py-1">Plan Date</th>
            <th className="text-left px-2 py-1">Check In</th>
            <th className="text-left px-2 py-1">Check Out</th>
            <th className="text-left px-2 py-1">Status</th>
            <th className="text-left px-2 py-1">Engineer</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {visits.map((v: any) => (
            <tr key={v.id}>
              <td className="px-2 py-1 font-medium text-gray-800 whitespace-nowrap">#{v.visit_number}</td>
              <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{formatDate(v.visit_date)}</td>
              <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{formatDateTime(v.check_in_time)}</td>
              <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{formatDateTime(v.check_out_time)}</td>
              <td className="px-2 py-1">
                <span className={`px-2 py-0.5 rounded-full whitespace-nowrap ${visitStatusTone[v.visit_status] || "bg-gray-100 text-gray-700"}`}>
                  {(v.visit_status || "").replace(/_/g, " ")}
                </span>
              </td>
              <td className="px-2 py-1 text-gray-600 whitespace-nowrap">{v.creator ? fullName(v.creator) : `#${v.created_by}`}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface WorkflowStepperProps {
  site: any;
  timeline: any[];
  visits: any[];
  visitSummary: VisitSummary;
  canSeeVisitDetails: boolean;
}
const WorkflowStepper: React.FC<WorkflowStepperProps> = ({
  site,
  timeline,
  visits,
  visitSummary,
  canSeeVisitDetails,
}) => {
  const timelineMap = useMemo(() => {
    const m = new Map<string, any>();
    timeline.forEach((r) => {
      if (r.to_status && !m.has(r.to_status)) m.set(r.to_status, r);
    });
    return m;
  }, [timeline]);

  const isCancelled = site.workflow_status === "cancelled" || site.status === "cancelled";
  const currentIndex = WORKFLOW_STAGES.findIndex((s) => s.value === site.workflow_status);

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-semibold text-gray-900">Workflow Progress</h3>
        {isCancelled && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <FiXCircle className="w-3 h-3" /> Cancelled
          </span>
        )}
      </div>
      <div className="space-y-3">
        {WORKFLOW_STAGES.map((step, index) => {
          const completed = !isCancelled && currentIndex >= 0 && index < currentIndex;
          const current = !isCancelled && index === currentIndex;
          const hist = timelineMap.get(step.value);
          return (
            <div key={step.value} className={`border rounded overflow-hidden ${current ? "border-blue-300 bg-blue-50/40" : "border-gray-200"}`}>
              <div className="w-full flex items-center gap-3 p-3 text-left">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  completed ? "bg-green-100 text-green-700" : current ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-400"
                }`}>
                  {completed ? <FiCheckCircle size={16} /> : current ? <FiActivity size={16} /> : <FiClock size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium flex flex-wrap items-center gap-2 ${completed || current ? "text-gray-900" : "text-gray-500"}`}>
                    {step.label}
                    {MATERIAL_STAGES.includes(step.value) && site.material_mode === "without_material" && (
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-orange-100 text-orange-800">
                        Skipped · {materialModeLabel(site.material_mode).toLowerCase()}
                      </span>
                    )}
                  </p>
                  {hist && (
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 flex-wrap">
                      {hist.performer && (<><FiUser className="inline" size={10} /> {fullName(hist.performer)}</>)}
                      {hist.created_at && <span>· {formatDateTime(hist.created_at)}</span>}
                    </p>
                  )}
                  {VISIT_STAGES.includes(step.value) && visitSummary.total > 0 && (
                    <>
                      <VisitCountPills summary={visitSummary} />
                      {canSeeVisitDetails && <VisitStepDetails visits={visits} />}
                    </>
                  )}
                </div>
                {current && <span className="px-2 py-1 bg-blue-100 text-blue-900 text-xs font-medium rounded-full whitespace-nowrap">Current</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Team members summary ─────────────────────────────────────────────────────
const memberTone = ["bg-blue-50 text-blue-900", "bg-purple-50 text-purple-900", "bg-orange-50 text-orange-900", "bg-green-50 text-green-900", "bg-indigo-50 text-indigo-900"];
const TeamMembersSummary: React.FC<{ assignments: any[] }> = ({ assignments }) => {
  if (!assignments.length) {
    return <p className="text-sm text-gray-500">No team members assigned yet.</p>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {assignments.map((a, i) => (
        <div key={a.id} className={`p-3 rounded ${memberTone[i % memberTone.length]}`}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/70 flex items-center justify-center text-xs font-bold flex-shrink-0">
              {initials(a.user)}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <p className="text-sm font-semibold truncate">{a.user ? fullName(a.user) : `#${a.user_id}`}</p>
                {a.is_supervisor && <FiStar className="w-3 h-3 text-amber-500 flex-shrink-0" />}
              </div>
              <p className="text-xs opacity-80 truncate">
                {a.user?.designation?.name || a.user?.role?.name || "Team Member"}
                {a.is_supervisor ? " · Supervisor" : ""}
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Status action modal (cancel / hold / resume) ─────────────────────────────
interface ActionModalState {
  open: boolean;
  action: "cancel" | "hold" | "resume" | null;
}
const StatusActionModal: React.FC<{
  state: ActionModalState;
  site: any;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ state, site, onClose, onSuccess }) => {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (state.open) {
      setReason("");
      setError("");
    }
  }, [state.open]);

  if (!state.open || !state.action) return null;
  const action = state.action;
  const meta = {
    cancel: { title: "Cancel Site", btn: "bg-red-600 hover:bg-red-700" },
    hold: { title: "Hold Site", btn: "bg-yellow-600 hover:bg-yellow-700" },
    resume: { title: "Resume Site", btn: "bg-green-600 hover:bg-green-700" },
  }[action];

  const submit = async () => {
    if (action !== "resume" && !reason.trim()) {
      setError("Please provide a reason.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await api.put(`/inc/sites/${site.id}/${action}`, { remarks: reason.trim() || undefined });
      toast.success(`Site ${action === "cancel" ? "cancelled" : action === "hold" ? "put on hold" : "resumed"}`);
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || "Action failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">{meta.title}: <span className="text-gray-600">{site.site_code}</span></h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FiX className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3">
          {action !== "resume" && (
            <textarea
              placeholder={`Reason for ${action}...`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
            />
          )}
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded">{error}</div>}
        </div>
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button onClick={onClose} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={submitting} className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded disabled:opacity-60 ${meta.btn}`}>
            {submitting ? "Submitting..." : meta.title}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Main view ────────────────────────────────────────────────────────────────
const ViewIncSite: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = useAuth();

  const [site, setSite] = useState<any>(null);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [visits, setVisits] = useState<any[]>([]);
  const [visitSummary, setVisitSummary] = useState<VisitSummary>(emptyVisitSummary);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "workflow");
  const [actionModal, setActionModal] = useState<ActionModalState>({ open: false, action: null });

  const canTimeline = hasPermission("IncSite", "timeline");
  const canViewVisits = hasPermission("IncSite", "visits");

  const fetchSite = useCallback(async () => {
    try {
      const res = await api.get(`/inc/sites/${id}`);
      setSite(res.data?.data);
    } catch {
      toast.error("Failed to load site");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAssignments = useCallback(async () => {
    try {
      const res = await api.get(`/inc/sites/${id}/assignments`);
      setAssignments(res.data?.data || []);
    } catch {
      /* non-fatal */
    }
  }, [id]);

  const fetchTimeline = useCallback(async () => {
    if (!canTimeline) return;
    try {
      const res = await api.get(`/inc/sites/${id}/timeline`);
      setTimeline(res.data?.data || []);
    } catch {
      /* non-fatal */
    }
  }, [id, canTimeline]);

  // Visit tallies for the field-work steps of the stepper. Someone who can see
  // visits gets the counts AND the per-visit lines; someone who can only see
  // the timeline still gets the counts, from the workflow-status API.
  const fetchVisits = useCallback(async () => {
    if (canViewVisits) {
      try {
        const res = await api.get(`/inc/sites/${id}/visits`, { params: { scope: 'all' } });
        const rows: any[] = res.data?.data || [];
        setVisits(rows);
        setVisitSummary(summariseVisits(rows));
        return;
      } catch {
        /* non-fatal */
      }
    }
    if (canTimeline) {
      try {
        const res = await api.get(`/inc/sites/${id}/workflow-status`);
        setVisits([]);
        setVisitSummary(res.data?.data?.visit_summary || emptyVisitSummary());
      } catch {
        /* non-fatal */
      }
    }
  }, [id, canViewVisits, canTimeline]);

  useEffect(() => {
    fetchSite();
    fetchAssignments();
    fetchTimeline();
    fetchVisits();
  }, [fetchSite, fetchAssignments, fetchTimeline, fetchVisits]);

  const changeTab = (key: string) => {
    setActiveTab(key);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const tabs = useMemo(
    () =>
      [
        { key: "workflow", label: "Workflow", icon: FiActivity, show: true },
        { key: "overview", label: "Overview", icon: FiInfo, show: true },
        { key: "team", label: "Team", icon: FiUsers, show: hasPermission("IncSite", "assign"), count: assignments.length },
        { key: "materials", label: "Material Planning", icon: FiBox, show: hasPermission("IncSite", "material_planning") },
        { key: "visits", label: "Visits", icon: FiTruck, show: hasPermission("IncSite", "visits"), count: visitSummary.total, attention: visitSummary.in_progress + visitSummary.planned },
        { key: "mb", label: "Measurement Book", icon: FiClipboard, show: hasPermission("IncSite", "measurement_book") },
        { key: "boq", label: "BOQ", icon: FiList, show: hasPermission("IncSite", "boq") },
        { key: "billing", label: "Billing", icon: FaIndianRupeeSign, show: hasPermission("IncSite", "billing") },
        { key: "claims", label: "INC Claims", icon: FiFileText, show: hasPermission("IncSite", "claim") },
        { key: "timeline", label: "Timeline", icon: FiClock, show: hasPermission("IncSite", "timeline") },
      ].filter((t) => t.show) as Array<{
        key: string;
        label: string;
        icon: React.ComponentType<{ size?: number }>;
        show: boolean;
        count?: number;
        attention?: number;
      }>,
    [hasPermission, assignments.length, visitSummary],
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-8 h-8 border-2 border-blue-800 border-t-transparent rounded-full animate-spin mb-3" />
          <span className="text-sm text-gray-600">Loading site details...</span>
        </div>
      </div>
    );
  }
  if (!site) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <FiXCircle className="w-10 h-10 text-red-500 mb-3" />
          <span className="text-sm text-gray-600">INC site not found.</span>
        </div>
      </div>
    );
  }

  const status = site.status;
  // Set by "Finalize Billing" on the Billing tab. A locked site is frozen: every
  // mutating INC endpoint answers 423, so the portal hides the controls rather
  // than letting the user find out by clicking. Each tab enforces it too — this
  // is the flag they all read.
  const isLocked = !!site.is_locked;
  const refreshAll = () => { fetchSite(); fetchAssignments(); fetchTimeline(); fetchVisits(); };

  // Dynamic workflow action — only when the site is actively progressing and the
  // user can perform the next step.
  const candidateAction = status === "in_progress" && !site.is_locked ? NEXT_ACTION[site.workflow_status] : undefined;
  const nextAction = candidateAction && hasPermission("IncSite", candidateAction.perm) ? candidateAction : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        <Link to="/inc/sites" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium mb-3">
          <FiArrowLeft className="w-4 h-4" /> Back to Sites
        </Link>

        {/* Header card */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-3 w-full">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-800 to-blue-500 rounded flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                {(site.site_code || "IN").substring(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900 truncate">{site.site_code}</h1>
                  <StatusBadge status={status} />
                  <MaterialModeBadge
                    mode={site.material_mode}
                    title={site.material_mode_remarks || undefined}
                  />
                </div>
                <p className="text-sm text-gray-600 mb-1">{site.site_name || "N/A"}</p>
                {isLocked && (
                  <p className="inline-flex items-center gap-1.5 mb-1 px-2 py-1 rounded text-xs font-medium text-purple-800 bg-purple-50 border border-purple-200">
                    <FiLock className="w-3 h-3 flex-shrink-0" />
                    Billing finalized{site.locked_at ? ` on ${formatDate(site.locked_at)}` : ""}
                    {site.locker ? ` by ${fullName(site.locker)}` : ""} — this site is locked and read-only.
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1 min-w-0 max-w-[220px] sm:max-w-[320px] lg:max-w-[520px]">
                    <FiMapPin size={12} /><span className="block truncate">{site.site_address || "No address"}</span>
                  </div>
                  <div className="flex items-center gap-1"><FiCalendar size={12} /> Created {formatDate(site.created_at)}</div>
                  <div className="flex items-center gap-1"><FiCalendar size={12} /> Updated {formatDate(site.updated_at)}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                {nextAction && (
                  <button
                    onClick={() => { changeTab(nextAction.tab); toast(`Complete "${nextAction.label}" below`, { icon: "➡️" }); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-white transition-colors ${nextAction.color}`}
                  >
                    <nextAction.icon className="w-4 h-4" /> {nextAction.label}
                  </button>
                )}
                {hasPermission("IncSite", "download_files") && (
                  <button
                    onClick={() => downloadIncFile(`/inc/sites/${site.id}/files/download-all`, `${site.site_code}-all-files.zip`)}
                    className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                    title="Download every file uploaded for this site as one ZIP"
                  >
                    <FiDownload className="w-4 h-4" /> <span className="hidden sm:inline">Download All Files</span>
                  </button>
                )}
                <button onClick={refreshAll} className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-blue-800 bg-blue-50 hover:bg-blue-100 transition-colors" title="Refresh">
                  <FiRefreshCw className="w-4 h-4" />
                </button>
                {hasPermission("IncSite", "edit") && status !== "cancelled" && !isLocked && (
                  <Link to={`/inc/${site.id}/update`} className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-blue-800 bg-blue-50 hover:bg-blue-100 transition-colors">
                    <FiEdit2 className="w-4 h-4" /> Edit
                  </Link>
                )}
                {hasPermission("IncSite", "hold") && status === "hold" && !isLocked && (
                  <button onClick={() => setActionModal({ open: true, action: "resume" })} className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-green-700 bg-green-50 hover:bg-green-100 transition-colors">
                    <FiPlay className="w-4 h-4" /> Resume
                  </button>
                )}
                {hasPermission("IncSite", "hold") && status !== "hold" && status !== "cancelled" && !isLocked && (
                  <button onClick={() => setActionModal({ open: true, action: "hold" })} className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100 transition-colors">
                    <FiPause className="w-4 h-4" /> Hold
                  </button>
                )}
                {hasPermission("IncSite", "cancel") && status !== "cancelled" && !isLocked && (
                  <button onClick={() => setActionModal({ open: true, action: "cancel" })} className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 transition-colors">
                    <FiXCircle className="w-4 h-4" /> Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((t) => {
                const Icon = t.icon;
                return (
                  <button
                    key={t.key}
                    onClick={() => changeTab(t.key)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === t.key
                        ? "border-blue-800 text-blue-800"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    <Icon size={16} /> {t.label}
                    {t.count !== undefined && t.count > 0 && (
                      <span
                        className={`px-1.5 py-0.5 text-xs rounded-full ${activeTab === t.key ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"}`}
                        title={t.attention ? `${t.attention} open` : undefined}
                      >
                        {t.count}
                      </span>
                    )}
                    {!!t.attention && <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title={`${t.attention} open`} />}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab content */}
        {activeTab === "workflow" && (
          <div className="space-y-6">
            <WorkflowStepper
              site={site}
              timeline={timeline}
              visits={visits}
              visitSummary={visitSummary}
              canSeeVisitDetails={canViewVisits}
            />
            <Section title="Team Members" icon={<FiUsers className="w-4 h-4 text-blue-800" />}>
              <TeamMembersSummary assignments={assignments} />
            </Section>
          </div>
        )}

        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Section title="Site Information" icon={<FiInfo className="w-4 h-4 text-blue-800" />}>
              <DetailRow label="Reference Number" value={site.reference_number} />
              <DetailRow label="Project" value={site.project?.project_name} />
              <DetailRow label="Activity" value={site.activity?.activity_name} />
              <DetailRow label="Client Project" value={site.client_project_name} />
              <DetailRow label="Work Type" value={site.work_type} />
              <DetailRow label="Priority" value={site.priority} />
              <DetailRow label="Team Type" value={site.team_type} />
              <DetailRow label="Vendor" value={site.vendor?.vendor_full_name} />
              <DetailRow
                label="Material"
                value={
                  <span className="inline-flex flex-wrap items-center gap-2">
                    <MaterialModeBadge mode={site.material_mode} showUndecided />
                    {site.material_mode_at && (
                      <span className="text-xs text-gray-500">
                        {formatDateTime(site.material_mode_at)}
                        {site.material_mode_user ? ` · ${fullName(site.material_mode_user)}` : ""}
                      </span>
                    )}
                    {site.material_mode === "without_material" && site.material_mode_remarks && (
                      <span className="block w-full text-xs text-gray-600">{site.material_mode_remarks}</span>
                    )}
                  </span>
                }
              />
            </Section>

            <Section title="Location" icon={<FiMapPin className="w-4 h-4 text-blue-800" />}>
              <DetailRow label="Site Address" value={site.site_address} />
              <DetailRow label="State" value={site.state_circle?.circle_name} />
              <DetailRow label="District" value={site.district?.name} />
              <DetailRow label="Pincode" value={site.pincode} />
              <DetailRow label="Latitude" value={site.latitude} />
              <DetailRow label="Longitude" value={site.longitude} />
            </Section>

            <Section title="Contact" icon={<FiClipboard className="w-4 h-4 text-blue-800" />}>
              <DetailRow label="Contact Person" value={site.contact_person} />
              <DetailRow label="Contact Number" value={site.contact_number} />
            </Section>

            <Section title="Requirements & Notes" icon={<FiInfo className="w-4 h-4 text-blue-800" />}>
              <DetailRow label="Work Requirement" value={site.work_requirement} />
              <DetailRow label="Customer Requirement" value={site.customer_requirement} />
              <DetailRow label="Remarks" value={site.remarks} />
              {site.cancel_reason && <DetailRow label="Cancel Reason" value={site.cancel_reason} />}
              {site.hold_reason && <DetailRow label="Hold Reason" value={site.hold_reason} />}
            </Section>
          </div>
        )}

        {activeTab === "team" && id && (
          <div className="space-y-6">
            <Section title="Team Members" icon={<FiUsers className="w-4 h-4 text-blue-800" />}>
              <TeamMembersSummary assignments={assignments} />
            </Section>
            <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><TeamTab siteId={id} locked={isLocked} /></div>
          </div>
        )}
        {activeTab === "materials" && id && <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><MaterialsTab siteId={id} site={site} onWorkflowChange={refreshAll} locked={isLocked} /></div>}
        {activeTab === "visits" && id && <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><VisitsTab siteId={id} onWorkflowChange={refreshAll} locked={isLocked} /></div>}
        {activeTab === "mb" && id && <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><MeasurementBookTab siteId={id} locked={isLocked} /></div>}
        {activeTab === "boq" && id && <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><BoqTab siteId={id} locked={isLocked} /></div>}
        {activeTab === "billing" && id && (
          <div className="space-y-6">
            <Section title="Purchase Order" icon={<FiClipboard className="w-4 h-4 text-blue-800" />}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6">
                <DetailRow label="PO Number" value={site.po_number} />
                <DetailRow label="PO Date" value={formatDate(site.po_date)} />
                <DetailRow label="Client Allocation Date" value={formatDate(site.client_allocation_date)} />
              </div>
            </Section>
            <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><BillingTab siteId={id} site={site} onWorkflowChange={refreshAll} /></div>
          </div>
        )}
        {activeTab === "claims" && id && <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><ClaimsTab siteId={id} locked={isLocked} /></div>}
        {activeTab === "timeline" && id && <div className="bg-white rounded shadow-sm border border-gray-200 p-4"><TimelineTab siteId={id} /></div>}
      </div>

      <StatusActionModal state={actionModal} site={site} onClose={() => setActionModal({ open: false, action: null })} onSuccess={refreshAll} />
    </div>
  );
};

export default ViewIncSite;
